import React, { useState, useMemo } from 'react';
import { Box, Typography, FormControl, FormLabel, Select, MenuItem, Chip, Divider, Accordion, AccordionSummary, AccordionDetails, Card, CardContent, Grid, Paper, Avatar } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaletteIcon from '@mui/icons-material/Palette';
import StraightenIcon from '@mui/icons-material/Straighten';
import StorageIcon from '@mui/icons-material/Storage';
import FormInput from '../../../inputs/FormInput';
import useCurrency from '../../../../hooks/common/useCurrency';

const VariantsTab = ({
    isEdit,
    isArabic,
    lng,
    selectedOptionGroups,
    optionValueIds,
    setOptionValueIds,
    optGroupOptions,
    isLoadingOpts,
    setSelectedOptionGroups,
    variantOverrides,
    setVariantOverrides
}) => {
    const [expandedPanel, setExpandedPanel] = useState(false);
    const { currency } = useCurrency();

    const getReadableTextColor = (backgroundColor) => {
        if (!backgroundColor || typeof backgroundColor !== 'string') return '#fff';
        const hex = backgroundColor.replace('#', '').trim();
        if (hex.length !== 6) return '#fff';
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.7 ? '#111' : '#fff';
    };

    const handleOptionGroupChange = (optionGroupId) => {
        const isSelected = selectedOptionGroups.some(og => og.id === optionGroupId);

        if (isSelected) {
            // Remove option group and its values
            setSelectedOptionGroups(prev => prev.filter(og => og.id !== optionGroupId));
            setOptionValueIds(prev => {
                const newValues = { ...prev };
                delete newValues[optionGroupId];
                return newValues;
            });
        } else {
            // Add option group
            const optionGroup = optGroupOptions.find(og => og.id === optionGroupId);
            if (optionGroup) {
                setSelectedOptionGroups(prev => [...prev, optionGroup]);
                setOptionValueIds(prev => ({
                    ...prev,
                    [optionGroupId]: []
                }));
            }
        }
    };

    const handleOptionValueChange = (optionGroupId, valueIds) => {
        setOptionValueIds(prev => ({
            ...prev,
            [optionGroupId]: valueIds
        }));
    };

    const handleSelectChange = (e) => {
        const newIds = e.target.value;
        const currentIds = selectedOptionGroups.map(og => og.id);

        // Find added and removed option groups
        const added = newIds.filter(id => !currentIds.includes(id));
        const removed = currentIds.filter(id => !newIds.includes(id));

        // Handle removed option groups
        removed.forEach(id => {
            handleOptionGroupChange(id);
        });

        // Handle added option groups
        added.forEach(id => {
            handleOptionGroupChange(id);
        });
    };

    // Generate all possible variant combinations
    const generatedVariants = useMemo(() => {
        if (selectedOptionGroups.length === 0) return [];

        const groups = selectedOptionGroups.map(og => {
            // Handle both create mode (from optGroupOptions) and edit mode (reconstructed)
            const optionValues = optionValueIds[og.id] || [];
            const availableValues = og.values || og.option_values || [];

            return {
                id: og.id,
                code: og.code,
                name: og.name?.[lng] || og.name?.en || og.code,
                values: optionValues.map(valueId => {
                    // Find value in both possible locations
                    const value = availableValues.find(v => v.id === valueId);
                    if (!value) return null;

                    return {
                        id: valueId,
                        value: value.value || valueId,
                        name: value.name?.[lng] || value.name?.en || value.value || valueId,
                        displayValue: value.value || valueId
                    };
                }).filter(v => v)
            };
        }).filter(g => g.values.length > 0);

        if (groups.length === 0) return [];

        // Generate all combinations
        const generateCombinations = (groups, index = 0, current = []) => {
            if (index === groups.length) {
                return [current];
            }

            const group = groups[index];
            const combinations = [];

            for (const value of group.values) {
                const newCurrent = [...current, { group: group, value: value }];
                combinations.push(...generateCombinations(groups, index + 1, newCurrent));
            }

            return combinations;
        };

        return generateCombinations(groups).map((combination, index) => {
            const variantKey = combination
                .map(c => `${c.group.code}:${c.value.displayValue}`)
                .sort() // Sort for consistent key generation
                .join('|');

            const variantName = combination
                .map(c => c.value.name)
                .join(' / ');

            return {
                key: variantKey,
                name: variantName,
                combination: combination,
                index: index
            };
        });
    }, [selectedOptionGroups, optionValueIds, lng]);

    // Handle variant override changes
    const handleVariantOverrideChange = (variantKey, field, value) => {
        setVariantOverrides(prev => {
            const existing = prev.find(v => v.variant_key === variantKey);

            const nextPrice = field === 'price'
                ? (value === '' ? null : (parseFloat(value) || null))
                : (existing?.price ?? null);

            const nextSku = field === 'sku'
                ? String(value || '')
                : String(existing?.sku || '');

            const shouldRemove = nextPrice == null && !nextSku;

            if (!existing) {
                if (shouldRemove) return prev;
                return [...prev, { variant_key: variantKey, price: nextPrice, sku: nextSku }];
            }

            if (shouldRemove) {
                return prev.filter(v => v.variant_key !== variantKey);
            }

            return prev.map(v =>
                v.variant_key === variantKey
                    ? { ...v, price: nextPrice, sku: nextSku }
                    : v
            );
        });
    };

    // Get override for a specific variant
    const getVariantOverride = (variantKey) => {
        return variantOverrides.find(v => v.variant_key === variantKey);
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Paper elevation={2} sx={{ p: 3, borderRadius: 2, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <Typography variant="h4" gutterBottom fontWeight="bold" color="white">
                        {isArabic ? 'إعدادات متغيرات المنتج' : 'Product Variants Settings'}
                    </Typography>
                    <Typography variant="body1" color="white" sx={{ opacity: 0.9 }}>
                        {isArabic
                            ? 'اختر مجموعات الخيارات لإنشاء متغيرات المنتج. سيتم إنشاء المتغيرات تلقائياً من جميع التوليفات الممكنة.'
                            : 'Select option groups to create product variants. Variants will be automatically generated from all possible combinations.'
                        }
                    </Typography>
                </Paper>
            </Box>

            {/* Option Groups Selection */}
            <Card elevation={1} sx={{ mb: 4 }}>
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                            <StraightenIcon />
                        </Avatar>
                        <Typography variant="h6" fontWeight="medium">
                            {isArabic ? 'مجموعات الخيارات' : 'Option Groups'}
                        </Typography>
                    </Box>
                    <FormControl fullWidth>
                        <FormLabel sx={{ mb: 2, fontWeight: 'medium' }}>
                            {isArabic ? 'اختر مجموعات الخيارات المتاحة' : 'Select Available Option Groups'}
                        </FormLabel>
                        <Select
                            multiple
                            value={selectedOptionGroups.map(og => og.id)}
                            onChange={handleSelectChange}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 1 }}>
                                    {selected.map((id) => {
                                        const og = optGroupOptions.find(o => o.id === id);
                                        return (
                                            <Chip
                                                key={id}
                                                label={og?.name?.[lng] || og?.name?.en || og?.code || id}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                            sx={{ minHeight: 56 }}
                        >
                            {optGroupOptions.map((og) => (
                                <MenuItem key={og.id} value={og.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography>
                                            {og.name?.[lng] || og.name?.en || og.code}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            ({(og.values || og.option_values || []).length} {isArabic ? 'قيم' : 'values'})
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </CardContent>
            </Card>

            <Divider sx={{ my: 3 }} />

            {/* Option Values Selection for each selected group */}
            {selectedOptionGroups.length > 0 && (
                <Card elevation={1} sx={{ mb: 4 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                                <PaletteIcon />
                            </Avatar>
                            <Typography variant="h6" fontWeight="medium">
                                {isArabic ? 'تفاصيل القيم المختارة' : 'Selected Values Details'}
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {isArabic
                                ? 'اختر القيم المطلوبة لكل مجموعة خيارات. يمكنك إضافة أو إزالة القيم بحرية.'
                                : 'Select the required values for each option group. You can add or remove values freely.'
                            }
                        </Typography>

                        {selectedOptionGroups.map((og, index) => (
                            <Box key={og.id} sx={{ mb: 3, p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
                                <FormControl fullWidth>
                                    <FormLabel sx={{ mb: 2, fontWeight: 'medium' }}>
                                        {isArabic ? 'قيم' : 'Values'} - {og.name?.[lng] || og.name?.en || og.code}
                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                            ({(og.values || og.option_values || []).length} {isArabic ? 'متاح' : 'available'})
                                        </Typography>
                                    </FormLabel>
                                    <Select
                                        multiple
                                        value={optionValueIds[og.id] || []}
                                        onChange={(e) => handleOptionValueChange(og.id, e.target.value)}
                                        renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, py: 1 }}>
                                                {selected.map((valueId) => {
                                                    // Handle both create mode (og.option_values) and edit mode (og.values)
                                                    const availableValues = og.values || og.option_values || [];
                                                    const value = availableValues.find(v => v.id === valueId);
                                                    if (!value) return null;

                                                    return (
                                                        <Chip
                                                            key={valueId}
                                                            label={value.name?.[lng] || value.name?.en || value.value || valueId}
                                                            size="small"
                                                            color="secondary"
                                                            variant="filled"
                                                            sx={{
                                                                backgroundColor: og.display_type === 'color' && value.value ? value.value : undefined,
                                                                color: og.display_type === 'color' && value.value ? getReadableTextColor(value.value) : undefined,
                                                                border: og.display_type === 'color' && value.value ? '2px solid' : undefined,
                                                                borderColor: og.display_type === 'color' && value.value ? 'divider' : undefined
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    >
                                        {(og.values || og.option_values || []).map((value) => (
                                            <MenuItem key={value.id} value={value.id}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    {og.display_type === 'color' && (
                                                        <Box
                                                            sx={{
                                                                width: 20,
                                                                height: 20,
                                                                borderRadius: '50%',
                                                                backgroundColor: value.value,
                                                                border: '1px solid',
                                                                borderColor: 'divider'
                                                            }}
                                                        />
                                                    )}
                                                    {og.display_type === 'image' && value.image && (
                                                        <Box
                                                            component="img"
                                                            src={value.image?.preview_url || value?.image}
                                                            alt={value.name?.[lng] || value.name?.en || value.value}
                                                            sx={{
                                                                width: 20,
                                                                height: 20,
                                                                objectFit: 'cover',
                                                                borderRadius: 1
                                                            }}
                                                        />
                                                    )}
                                                    <Typography>
                                                        {value.name?.[lng] || value.name?.en || value.value}
                                                    </Typography>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* Preview of generated variants */}
            {selectedOptionGroups.length > 0 && (
                <Card elevation={3} sx={{ mt: 4 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                            <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                                <StorageIcon />
                            </Avatar>
                            <Typography variant="h6" fontWeight="medium">
                                {isArabic ? 'معاينة المتغيرات' : 'Variants Preview'}
                            </Typography>
                        </Box>

                        <>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} md={6}>
                                    <Paper
                                        elevation={2}
                                        sx={(theme) => ({
                                            p: 2,
                                            textAlign: 'center',
                                            bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.18 : 0.08),
                                            border: '2px solid',
                                            borderColor: 'success.main'
                                        })}
                                    >
                                        <Typography variant="h3" color="success.main" fontWeight="bold">
                                            {generatedVariants.length}
                                        </Typography>
                                        <Typography variant="body2" color="success.dark">
                                            {isArabic ? 'متغير سيتم إنشاؤه' : 'variants will be created'}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Paper elevation={1} sx={{ p: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            {isArabic ? 'تركيبة المتغيرات:' : 'Variant combinations:'}
                                        </Typography>
                                        <Typography variant="body1" fontWeight="medium">
                                            {selectedOptionGroups.map(og =>
                                                `${og.name?.[lng] || og.name?.en || og.code} (${(og.values || og.option_values || []).length})`
                                            ).join(' × ')}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {isArabic
                                ? 'سيتم إنشاء هذه المتغيرات تلقائياً من جميع التوليفات الممكنة للخيارات المختارة.'
                                : 'These variants will be automatically generated from all possible combinations of selected options.'
                            }
                        </Typography>

                        {/* Variant Overrides Accordion */}
                        <Accordion
                            expanded={expandedPanel}
                            onChange={() => setExpandedPanel(!expandedPanel)}
                            sx={{ boxShadow: 2, '&:before': { display: 'none' }, bgcolor: 'background.paper' }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={(theme) => ({
                                    bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.08),
                                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.26 : 0.12) },
                                    borderRadius: 1
                                })}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                                        <Typography variant="body2" fontWeight="bold" color="white">
                                            {variantOverrides.length}
                                        </Typography>
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="medium">
                                            {isArabic ? 'الأسعار المخصصة' : 'Custom Pricing'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {isArabic
                                                ? `${variantOverrides.length} متغير بأسعار مخصصة`
                                                : `${variantOverrides.length} variants with custom pricing`
                                            }
                                        </Typography>
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 0 }}>
                                <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {isArabic
                                            ? 'يمكنك تحديد أسعار أو SKU خاصة لبعض المتغيرات. اترك الحقول فارغة لاستخدام السعر الافتراضي.'
                                            : 'You can set custom prices or SKUs for specific variants. Leave fields empty to use default pricing.'
                                        }
                                    </Typography>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {generatedVariants.map((variant) => {
                                            const override = getVariantOverride(variant.key);
                                            const hasCustomPrice = override?.price != null;

                                            return (
                                                <Box
                                                    key={variant.key}
                                                    sx={(theme) => ({
                                                        p: 2,
                                                        border: '1px solid',
                                                        borderColor: hasCustomPrice ? theme.palette.success.main : theme.palette.divider,
                                                        borderRadius: 2,
                                                        bgcolor: hasCustomPrice
                                                            ? alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.16 : 0.08)
                                                            : theme.palette.background.paper,
                                                        transition: 'all 0.2s ease',
                                                        '&:hover': {
                                                            boxShadow: theme.shadows[2]
                                                        }
                                                    })}
                                                >
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight="medium">
                                                            {variant.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {variant.key}
                                                        </Typography>
                                                    </Box>

                                                    <Grid container spacing={2} alignItems="center">
                                                        <Grid item xs={12} md={6}>
                                                            <FormInput
                                                                label={isArabic ? `السعر (${currency})` : `Price (${currency})`}
                                                                type="number"
                                                                name={`variant_price_${variant.key}`}
                                                                required={false}
                                                                value={override?.price ?? ''}
                                                                placeholder={isArabic ? 'اتركه فارغ لاستخدام السعر الافتراضي' : 'Leave empty to use default price'}
                                                                onChange={(e) => handleVariantOverrideChange(variant.key, 'price', e.target.value)}
                                                                sx={{ width: '100%', height: 44, borderRadius: 1 }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <FormInput
                                                                label={isArabic ? 'SKU' : 'SKU'}
                                                                type="text"
                                                                name={`variant_sku_${variant.key}`}
                                                                required={false}
                                                                value={override?.sku ?? ''}
                                                                placeholder={isArabic ? 'اتركه فارغ للتلقائي' : 'Leave empty for auto'}
                                                                onChange={(e) => handleVariantOverrideChange(variant.key, 'sku', e.target.value)}
                                                                sx={{ width: '100%', height: 44, borderRadius: 1 }}
                                                            />
                                                        </Grid>
                                                    </Grid>

                                                    {/* Show variant combination details */}
                                                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                        {variant.combination.map((c, idx) => (
                                                            <Chip
                                                                key={idx}
                                                                size="small"
                                                                label={`${c.group.name}: ${c.value.name}`}
                                                                variant="outlined"
                                                                sx={{ fontSize: '0.7rem' }}
                                                            />
                                                        ))}
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default VariantsTab;
